import React from 'react';

const CreditTransactions = ({ transactions, pagination, setPagination, onRefresh, colorTheme }) => {
  const getThemeClasses = () => {
    if (colorTheme === 'purple') {
      return {
        button: 'bg-purple-600 hover:bg-purple-700 text-white',
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      };
    } else if (colorTheme === 'blue') {
      return {
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      };
    }
    return {
      button: 'bg-green-600 hover:bg-green-700 text-white',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
  };

  const theme = getThemeClasses();

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'purchase':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'usage':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'refund':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'admin_adjustment':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase':
        return '💳';
      case 'usage':
        return '📊';
      case 'refund':
        return '↩️';
      case 'admin_adjustment':
        return '⚙️';
      default:
        return '📝';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadMore = () => {
    setPagination({
      ...pagination,
      skip: pagination.skip + pagination.limit
    });
    onRefresh();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Transaction History</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Complete audit trail of all credit transactions
          </p>
        </div>
        <button
          onClick={onRefresh}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${theme.button}`}
        >
          🔄 Refresh
        </button>
      </div>

      {transactions.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">📝</div>
          <p className="text-xl text-gray-500 dark:text-gray-400">No transactions yet</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
            Credit transactions will appear here
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          {/* Desktop View - Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Balance After
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeColor(txn.transaction_type)}`}>
                        <span className="mr-1">{getTransactionIcon(txn.transaction_type)}</span>
                        {txn.transaction_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white font-medium">
                        {txn.description}
                      </div>
                      {txn.ad_type && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Ad Type: {txn.ad_type}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold ${
                        txn.amount >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {txn.amount >= 0 ? '+' : ''}{txn.amount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-white font-medium">
                        {txn.balance_after}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {txn.price_paid_usd ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            ${txn.price_paid_usd.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {txn.payment_method || 'N/A'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(txn.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View - Cards */}
          <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {transactions.map((txn) => (
              <div key={txn.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeColor(txn.transaction_type)}`}>
                    <span className="mr-1">{getTransactionIcon(txn.transaction_type)}</span>
                    {txn.transaction_type}
                  </span>
                  <span className={`text-lg font-bold ${
                    txn.amount >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {txn.amount >= 0 ? '+' : ''}{txn.amount}
                  </span>
                </div>
                
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  {txn.description}
                </p>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Balance After:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">{txn.balance_after}</span>
                  </div>
                  {txn.price_paid_usd && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Payment:</span>
                      <span className="ml-1 font-medium text-gray-900 dark:text-white">${txn.price_paid_usd.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {formatDate(txn.created_at)}
                </p>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {transactions.length >= pagination.limit && (
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={loadMore}
                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${theme.button}`}
              >
                Load More Transactions
              </button>
            </div>
          )}
        </div>
      )}

      {/* Summary Footer */}
      {transactions.length > 0 && (
        <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
          Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default CreditTransactions;
